import { inject, Injectable } from '@angular/core';
import { CanActivate, GuardResult, MaybeAsync, Router } from '@angular/router';
import { SplashService } from '@context/frontend/common';
import { getUserStatus } from '@context/shared/types/user';
import { filter, map, race, Subject, take, takeUntil, timer } from 'rxjs';
import { AuthService } from '../services';

/** Max time to wait for auth to settle before denying activation. */
const AUTH_GUARD_TIMEOUT_MS = 15_000;

@Injectable({ providedIn: 'root' })
export class CustomAuthGuard implements CanActivate {
	private readonly authService = inject(AuthService);
	private readonly router = inject(Router);
	private readonly splash = inject(SplashService);

	canActivate(): MaybeAsync<GuardResult> {
		this.splash.onShow();
		const cancelled$ = new Subject<void>();

		return new Promise<boolean>((resolve) => {
			const authReady$ = this.authService.authenticated$.pipe(
				filter((authenticated) => authenticated === true),
				map(() => {
					// Invite / external share sessions must not enter the main app.
					if (
						this.authService.inviteId() ||
						this.authService.shareExternalId()
					) {
						return false;
					}

					if (!this.authService.user) {
						return null;
					}

					const status = getUserStatus(this.authService.user);
					return status !== 'disabled' && status !== 'deleted';
				}),
				filter(
					(canActivate): canActivate is boolean =>
						canActivate !== null,
				),
				take(1),
			);

			const timeout$ = timer(AUTH_GUARD_TIMEOUT_MS).pipe(
				map(() => false),
			);

			race(authReady$, timeout$)
				.pipe(takeUntil(cancelled$))
				.subscribe({
					next: (canActivate) => {
						cancelled$.next();
						resolve(canActivate);
					},
					error: () => {
						cancelled$.next();
						resolve(false);
					},
				});
		})
			.then(async (canActivate) => {
				if (!canActivate) {
					await this.authService.signOut();
					await this.router.navigate(['/auth']);
					return false;
				}
				return true;
			})
			.finally(() => {
				this.splash.onHide();
			});
	}
}
